import {ObjectOf} from 'src/types/store';
import {AppDeclaration} from 'src/apps/types';
import {EducationPhase, UserIdentifier, UserType} from 'src/types/enums';
import {lazy} from 'react';
import {hsReducers} from 'src/views/high-school/reducers';
import {transferReducers} from 'src/views/transfer/reducers';
import {adminReducers} from 'src/views/admin/reducers';
import {undergraduateReducers} from 'undergraduate-student/reducers';
import onboardingReducers from 'shared/student/registration/reducers';
import {parentReducers} from 'src/views/parent/reducers';
import {reducers as collegePortalReducers} from 'src/redux/college-portal/reducers';
import {collegePortalInitialState} from 'src/redux/student-search/initial-state';
import {districtAdminReducers} from 'district-admin/reducers';
import {educatorReducers} from '../../educator/app/reducers/root-reducer';

const educatorInitialState = {
  collegeProfile: {
    userInfo: {
      user: UserType.EDUCATOR,
    },
    tab: 0,
    loading: true,
    modalPending: {},
  },
};

export const apps: ObjectOf<AppDeclaration> = {
  [UserIdentifier.HS_STUDENT]: {
    app: lazy(() => import('src/views/high-school')),
    mobileApp: lazy(() => import('src/views/high-school/high-school-mobile-application')),
    reducers: hsReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.HIGH_SCHOOL,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
    },
  },
  [UserIdentifier.CC_STUDENT]: {
    app: lazy(() => import('src/views/transfer')),
    mobileApp: lazy(() => import('src/views/transfer/transfer-mobile-application')),
    reducers: transferReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.COMMUNITY_COLLEGE,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
      transfer: true,
    },
  },
  [UserIdentifier.UG_STUDENT]: {
    app: lazy(() => import('src/views/undergraduate')),
    mobileApp: lazy(() => import('src/views/undergraduate/undergraduate-mobile-application')),
    reducers: undergraduateReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.UNDERGRADUATE,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
    },
  },
  [UserIdentifier.ADULT_LEARNER]: {
    app: lazy(() => import('src/views/adult-learner')),
    mobileApp: lazy(() => import('src/views/adult-learner/adult-learner-mobile-application')),
    reducers: undergraduateReducers,
    initialState: {
      collegeProfile: {
        userInfo: {
          user: EducationPhase.ADULT_LEARNER,
        },
        tab: 0,
        loading: true,
        modalPending: {},
      },
    },
  },
  [UserIdentifier.ADMIN]: {
    app: lazy(() => import('src/views/admin')),
    reducers: adminReducers,
    initialState: {},
  },
  [UserIdentifier.PARENT]: {
    app: lazy(() => import('src/views/parent/index')),
    mobileApp: lazy(() => import('src/views/parent/parent-mobile-application')),
    reducers: parentReducers,
    initialState: {parent: {}},
  },
  [UserIdentifier.CP_USER]: {
    app: lazy(() => import('src/views/college-portal/index')),
    reducers: collegePortalReducers,
    initialState: collegePortalInitialState,
  },
  [UserIdentifier.DISTRICT_ADMIN]: {
    app: lazy(() => import('src/views/district-admin')),
    reducers: districtAdminReducers,
    initialState: {user: {type: 'DistrictAdmin'}},
  },
  [UserIdentifier.TEACHER]: {
    app: lazy(() => import('src/views/educator')),
    mobileApp: lazy(() => import('src/views/educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
  [UserIdentifier.COUNSELOR]: {
    app: lazy(() => import('src/views/educator')),
    mobileApp: lazy(() => import('src/views/educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
  [UserIdentifier.MENTOR]: {
    app: lazy(() => import('src/views/educator')),
    mobileApp: lazy(() => import('src/views/educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
  [UserIdentifier.CC_STAFF]: {
    app: lazy(() => import('src/views/educator')),
    mobileApp: lazy(() => import('src/views/educator/educator-mobile-application')),
    reducers: educatorReducers,
    initialState: educatorInitialState,
  },
};

export const onboardingApps: ObjectOf<AppDeclaration> = {
  [UserIdentifier.HS_STUDENT]: {
    app: lazy(() => import('src/views/high-school/onboarding')),
    mobileApp: lazy(() => import('src/views/high-school/onboarding/high-school-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {onboarding: true},
  },
  [UserIdentifier.CC_STUDENT]: {
    app: lazy(() => import('src/views/transfer/onboarding')),
    mobileApp: lazy(() => import('src/views/transfer/onboarding/transfer-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {onboarding: true},
  },

  [UserIdentifier.PARENT]: {
    app: lazy(() => import('src/views/parent/onboarding')),
    mobileApp: lazy(() => import('src/views/parent/onboarding/parent-mobile-onboarding')),
    reducers: parentReducers,
    initialState: {onboarding: true},
  },
  [UserIdentifier.ADULT_LEARNER]: {
    app: lazy(() => import('src/views/adult-learner/onboarding')),
    mobileApp: lazy(
      () => import('src/views/adult-learner/onboarding/adult-leaner-mobile-onboarding')
    ),
    reducers: onboardingReducers,
    initialState: {onboarding: true},
  },
  [UserIdentifier.TEACHER]: {
    app: lazy(() => import('src/views/educator/onboarding')),
    mobileApp: lazy(() => import('src/views/educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}, onboarding: true},
  },
  [UserIdentifier.COUNSELOR]: {
    app: lazy(() => import('src/views/educator/onboarding')),
    mobileApp: lazy(() => import('src/views/educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}, onboarding: true},
  },
  [UserIdentifier.MENTOR]: {
    app: lazy(() => import('src/views/educator/onboarding')),
    mobileApp: lazy(() => import('src/views/educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}, onboarding: true},
  },
  [UserIdentifier.CC_STAFF]: {
    app: lazy(() => import('src/views/educator/onboarding')),
    mobileApp: lazy(() => import('src/views/educator/onboarding/educator-mobile-onboarding')),
    reducers: onboardingReducers,
    initialState: {educator: {}, onboarding: true},
  },
  [UserIdentifier.CP_USER]: {
    app: lazy(() => import('src/views/college-portal/reset-password')),
    reducers: collegePortalReducers,
    initialState: collegePortalInitialState,
  },
};
